import { isEqual, isNil } from 'lodash';
import moment from 'moment';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ScrollView } from 'react-native';
import { Options as RRuleOptions } from 'rrule';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { useCardBlockScrollContext } from '@shared/components/blocks/contexts/scroll';
import { MBComboDatePicker } from '@shared/components/dateAndTime/comboDatePicker';
import { DatePickerValue } from '@shared/components/dateAndTime/comboDatePicker/props';
import { DateField } from '@shared/data/entity/fields/DateField';
import { useBatch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext/BatchContext';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useIsDesktop, useIsMobile } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { BatchScheduleComponent } from './BatchSchedule';
import { RepeatTextButton } from './RepeatTextButton';
import { RepeatEndType } from './types';
import { getHighlightDates } from './utils';

interface DateEditFieldPickerProps {
  onClose(): void;
  anchorName: string;
  field: DateField | null;
  component: API.DateComponent;
  updateField?(componentId: string, field: DateField | null): void;
  repeatPresets: Partial<RRuleOptions>[];
  filteredRepeatPresets: Partial<RRuleOptions>[];
  repeatCount: number | undefined;
  setRepeatCount: Dispatch<SetStateAction<number | undefined>>;
  repeatUntil: Date | undefined;
  setRepeatUntil: Dispatch<SetStateAction<Date | undefined>>;
  repeatEndType: RepeatEndType;
  setRepeatEndType: Dispatch<SetStateAction<RepeatEndType>>;
  presetIndexSelected: number;
  setPresetIndexSelected: Dispatch<SetStateAction<number>>;
  isCustomRepeatSelected: boolean;
  isOffsetSelected: boolean;
  resetBatchOptions: () => void;
  setUserIsEditing: Dispatch<SetStateAction<boolean>>;
  workflowTemplateId: string;
}

export function DateEditFieldPicker({
  field,
  component,
  updateField,
  onClose,
  anchorName,
  repeatPresets,
  filteredRepeatPresets,
  repeatCount,
  setRepeatCount,
  repeatUntil,
  setRepeatUntil,
  repeatEndType,
  setRepeatEndType,
  presetIndexSelected,
  setPresetIndexSelected,
  isCustomRepeatSelected,
  isOffsetSelected,
  resetBatchOptions,
  setUserIsEditing,
  workflowTemplateId,
}: DateEditFieldPickerProps) {
  const {
    setRruleOptions,
    rruleOptions,
    canBatchCreate,
    minimumInSeries,
    setMinimumInSeries,
    tagFilter,
    setTagFilter,
    offset,
    setOffset,
    recurrenceType,
    setRecurrenceType,
  } = useBatch();
  const isBatchRuleEnabled = canBatchCreate && component.allowEntityBatchCreation;
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const [showRepeatOptions, setShowRepeatOptions] = useState(
    !isNil(rruleOptions) && !!isBatchRuleEnabled && !isMobile,
  );
  const [showingCustomOptions, setShowingCustomOptions] = useState(false);
  const [showingTagFilterOptions, setShowingTagFilterOptions] = useState(false);
  const [showingEndDateOptions, setShowingEndDateOptions] = useState(false);
  const isShowingMobileRepeatOptions = showRepeatOptions && isMobile;
  const scrollViewRef = useCardBlockScrollContext();
  const [highlightDates, setHighlightDates] = useState<string[] | undefined>();

  // If no rule yet, set the first preset as the default when the
  // repeat options panel is opened.
  useEffect(() => {
    if (
      !isNil(rruleOptions) ||
      isCustomRepeatSelected ||
      !showRepeatOptions ||
      !isBatchRuleEnabled
    ) {
      return;
    }
    setRruleOptions(filteredRepeatPresets[presetIndexSelected]);
    setUserIsEditing(true);
  }, [
    rruleOptions,
    setRruleOptions,
    presetIndexSelected,
    filteredRepeatPresets,
    isCustomRepeatSelected,
    showRepeatOptions,
    isBatchRuleEnabled,
    setUserIsEditing,
  ]);

  const onUpdateDate = useCallback(
    (newDate: DatePickerValue) => {
      updateField?.(component.id, !isNil(newDate.start) ? new DateField(newDate.start) : null);
      if (isNil(newDate.start)) {
        resetBatchOptions();
        setShowRepeatOptions(false);
      }
      setUserIsEditing(true);
    },
    [component.id, updateField, resetBatchOptions, setUserIsEditing],
  );

  const renderTitleBar = useCallback(() => {
    if (isDesktop) {
      return null;
    }

    if (isShowingMobileRepeatOptions && showingTagFilterOptions) {
      return (
        <AnchoredSheetTitleBar
          title={i18n('workflowTemplate.component.dateAndRepeat.autoExtend.tagFilterLabel')}
          rightButtonText={i18n('common.done')}
          onRightIconPress={() => setShowingTagFilterOptions(false)}
        />
      );
    }

    if (isShowingMobileRepeatOptions && showingEndDateOptions) {
      return (
        <AnchoredSheetTitleBar
          title={i18n('workflowTemplate.component.dateAndRepeat.endsOn')}
          rightButtonText={i18n('common.done')}
          onRightIconPress={() => setShowingEndDateOptions(false)}
        />
      );
    }

    if (showRepeatOptions && !showingCustomOptions) {
      return (
        <AnchoredSheetTitleBar
          title={i18n('workflowTemplate.component.dateAndRepeat.repeat')}
          titleSize={isDesktop ? 16 : undefined}
          height={isDesktop ? grid(5) : undefined}
          leftIcon="arrow-point-left"
          leftIconColor={Colors.v2.purple}
          leftIconSize={isDesktop ? grid(3.25) : undefined}
          onLeftIconPress={() => setShowRepeatOptions(false)}
        />
      );
    }

    if (isShowingMobileRepeatOptions && showingCustomOptions) {
      return (
        <AnchoredSheetTitleBar
          title={i18n('workflowTemplate.component.dateAndRepeat.customRepeat')}
          leftIcon="arrow-point-left"
          leftIconColor={Colors.v2.purple}
          onLeftIconPress={() => setShowingCustomOptions(false)}
        />
      );
    }

    return (
      <AnchoredSheetTitleBar
        title={component.label ?? ''}
        rightIcon="exit"
        rightIconColor={colors.foregroundActive}
        onRightIconPress={onClose}
      />
    );
  }, [
    colors.foregroundActive,
    component.label,
    onClose,
    isDesktop,
    isShowingMobileRepeatOptions,
    showingCustomOptions,
    showRepeatOptions,
    showingTagFilterOptions,
    showingEndDateOptions,
    setShowingEndDateOptions,
  ]);

  const handleClearRepeat = () => {
    if (isBatchRuleEnabled) {
      setShowRepeatOptions(false);
      resetBatchOptions();
    }
    setUserIsEditing(true);
  };

  const handleAddRepeat = () => {
    setShowRepeatOptions(true);
    setUserIsEditing(true);
  };

  const showHighlightDates =
    !isNil(rruleOptions) && component.allowEntityBatchCreation && canBatchCreate;
  const showCalendar = isDesktop || !showRepeatOptions;

  const computeHighlightDates = useCallback(
    (monthStartISO: string) => {
      const dates = getHighlightDates({ rruleOptions, monthStartISO });
      setHighlightDates(prev => (isEqual(prev, dates) ? prev : dates.length ? dates : undefined));
    },
    [rruleOptions],
  );

  // Compute initial highlights when rule or selected date changes
  useEffect(() => {
    if (!rruleOptions) {
      setHighlightDates(undefined);
      return;
    }

    const referenceDateISO = field?.date ?? moment().toISOString();
    computeHighlightDates(referenceDateISO);
  }, [rruleOptions, field?.date, computeHighlightDates]);

  const highlightProps = useMemo(
    () => (showHighlightDates ? { highlightDates, onMonthChange: computeHighlightDates } : {}),
    [showHighlightDates, highlightDates, computeHighlightDates],
  );

  const showRepeatTextButton =
    isBatchRuleEnabled && !showingTagFilterOptions && !showingEndDateOptions;

  return (
    <AnchoredSheet
      initialSize="full"
      onClose={onClose}
      renderTitleBar={renderTitleBar}
      anchorName={anchorName}
      aria-label={getComponentLabel(component)}
    >
      <ScrollView
        keyboardShouldPersistTaps="always"
        ref={scrollViewRef}
        contentContainerStyle={{
          width: '100%',
          alignItems: 'flex-start',
          paddingBottom: isMobile ? grid(4) : undefined,
          flexGrow: 1,
          flexShrink: 0,
          flexBasis: 'auto',
        }}
      >
        <View
          style={{
            flexDirection: isDesktop ? 'row' : 'column',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <View style={{ width: isDesktop ? grid(37) : '100%' }}>
            {showCalendar && (
              <MBComboDatePicker
                label={component.label ?? ''}
                value={isNil(field?.date) ? undefined : { start: field?.date }}
                onChange={onUpdateDate}
                mode={{ date: 'single', time: 'single' }}
                {...highlightProps}
                layoutStyle={{ justifyContent: 'flex-start', minHeight: 'auto', flex: 0 }}
              />
            )}
            {showRepeatTextButton && (
              <View style={{ width: isDesktop ? grid(37) : '100%' }}>
                <RepeatTextButton
                  onPress={handleAddRepeat}
                  disabled={isNil(field?.date)}
                  isConfiguring={showRepeatOptions}
                  rruleOptions={rruleOptions}
                  offset={offset}
                  tagFilter={tagFilter}
                  workflowTemplateId={workflowTemplateId}
                  onClear={handleClearRepeat}
                />
              </View>
            )}
          </View>
          {showRepeatOptions && isBatchRuleEnabled && (
            <BatchScheduleComponent
              date={field?.date}
              setRruleOptions={setRruleOptions}
              rruleOptions={rruleOptions}
              showingCustomOptions={showingCustomOptions}
              setShowingCustomOptions={setShowingCustomOptions}
              showingTagFilterOptions={showingTagFilterOptions}
              setShowingTagFilterOptions={setShowingTagFilterOptions}
              presets={repeatPresets}
              filteredPresets={filteredRepeatPresets}
              count={repeatCount}
              setCount={setRepeatCount}
              until={repeatUntil}
              setUntil={setRepeatUntil}
              repeatEndType={repeatEndType}
              setRepeatEndType={setRepeatEndType}
              presetIndexSelected={presetIndexSelected}
              setPresetIndexSelected={setPresetIndexSelected}
              isCustomSelected={isCustomRepeatSelected}
              isOffsetSelected={isOffsetSelected}
              setUserIsEditing={setUserIsEditing}
              minimumInSeries={minimumInSeries}
              setMinimumInSeries={setMinimumInSeries}
              tagFilter={tagFilter}
              setTagFilter={setTagFilter}
              component={component}
              workflowTemplateId={workflowTemplateId}
              showingEndDateOptions={showingEndDateOptions}
              setShowingEndDateOptions={setShowingEndDateOptions}
              offset={offset}
              setOffset={setOffset}
              recurrenceType={recurrenceType}
              setRecurrenceType={setRecurrenceType}
            />
          )}
        </View>
      </ScrollView>
    </AnchoredSheet>
  );
}
